import { useCallback, useState } from "react";

type Location = {
  latitude: number;
  longitude: number;
};

export type PlacePrediction = {
  description: string;
  id: string;
  types?: string[];
};

type AutocompleteRequest = {
  input: string;
  includedPrimaryTypes?: string[];
  maxResultCount?: number;
  locationBias?: {
    circle: { center: Location; radius: number };
  };
  languageCode?: string;
  regionCode?: string;
};

export type AutocompleteOptions = {
  location?: Location;
  radius?: number;
  includedTypes?: string[];
};

type PlaceDto = {
  placeId: string;
  text: {
    text: string;
  };
  types?: string[];
};

type AutocompleteResponse = {
  suggestions: Record<"placePrediction" | "queryPrediction", PlaceDto>[];
};

export const useAutocomplete = () => {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [loading, setLoading] = useState(false);

  const searchPredictions = useCallback(
    async (searchText: string, options: AutocompleteOptions = {}): Promise<PlacePrediction[]> => {
      const { radius = 1000, includedTypes = [], location } = options;

      setLoading(true);

      try {
        const requestBody: AutocompleteRequest = {
          input: searchText,
        };

        if (includedTypes.length > 0) {
          requestBody.includedPrimaryTypes = includedTypes;
        }

        if (location) {
          requestBody.locationBias = {
            circle: {
              center: {
                latitude: location.latitude,
                longitude: location.longitude,
              },
              radius,
            },
          };
        }

        const response = await fetch(`https://places.googleapis.com/v1/places:autocomplete`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": process.env["NX_PUBLIC_GOOGLE_MAPS_API_KEY"]!,
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`API Error: ${response.status} - ${errorData.error?.message || "Unknown error"}`);
        }

        const data: AutocompleteResponse = await response.json();
        const parsedPredictions = data.suggestions
          .filter((suggestion) => !!suggestion.placePrediction)
          .map((suggestion) => ({
            description: suggestion.placePrediction.text.text,
            id: suggestion.placePrediction.placeId,
            types: suggestion.placePrediction.types,
          }));
        setPredictions(parsedPredictions || []);
        return parsedPredictions || [];
      } catch (err) {
        return [];
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    predictions,
    loading,
    searchPredictions,
    setPredictions,
  };
};
